import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import {
    Plus, Search, Edit2, Trash2, X, Save, MessageSquare, CheckCircle, Clock, UserPlus, User
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface TestPrepLead {
    id: string;
    full_name: string;
    category: string;
    contact_date: string;
    phone: string;
    email: string;
    communication_type: string;
    interested_exam: string;
    request_type: string;
    notes: string;
    is_registered: string;
    created_by?: string;
    creator?: { name: string } | null;
    advising_type?: string;
}

const EXAMS = [
    'SAT', 'IELTS', 'TOEFL', 'IMAT', 'TIL-I', 'TEST-ARCH', 'TIL-A',
    'TOLC-I', 'TOLC-SU', 'TOLC-PSI', 'TOLC-F', 'TOLC-E', 'TOLC-AV', 'TOLC-S',
    'TOLC-B', 'TOLC-LP', 'TOLC-SPS', 'BOCCONI TEST', 'CENT-S', 'ITA BOCCONI TEST',
    'IYA TIL-I', 'ITA TIL-I', 'TOLD-D', 'TIL-D', 'ITA TOL-D', 'CILS', 'CELI'
];

const COMMUNICATION_TYPES = ['Fiziki', 'WhatsApp', 'Mail', 'Telefon', 'Instagram', 'Online'];
const REQUEST_TYPES = ['Kurs', 'Özel Ders', 'Deneme Sınavı', 'Koçluk', 'Döküman Hizmetleri'];
const CATEGORIES = ['Veli', 'Öğrenci'];

const TestPrepLeads: React.FC = () => {
    const { user } = useAuth();
    const [leads, setLeads] = useState<TestPrepLead[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showSmsModal, setShowSmsModal] = useState(false);
    const [editingLead, setEditingLead] = useState<TestPrepLead | null>(null);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

    const [formData, setFormData] = useState<Partial<TestPrepLead>>({
        full_name: '',
        category: 'Öğrenci',
        contact_date: new Date().toISOString().split('T')[0],
        phone: '',
        email: '',
        communication_type: 'Telefon',
        interested_exam: 'SAT',
        request_type: 'Kurs',
        notes: '',
        is_registered: 'Hayır',
        advising_type: 'Danışmanlık Dışı'
    });

    const [smsMessage, setSmsMessage] = useState('');
    const [sendingSms, setSendingSms] = useState(false);

    useEffect(() => {
        fetchLeads();
    }, []);

    const fetchLeads = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('testprep_leads')
                .select('*, creator:profiles(name)')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setLeads(data || []);
        } catch (error) {
            console.error('Error fetching testprep leads:', error);
            toast.error('Kayıtlar yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const saveFields = {
                full_name: formData.full_name,
                category: formData.category,
                contact_date: formData.contact_date,
                phone: formData.phone,
                email: formData.email,
                communication_type: formData.communication_type,
                interested_exam: formData.interested_exam,
                request_type: formData.request_type,
                notes: formData.notes,
                is_registered: formData.is_registered,
                advising_type: formData.advising_type || 'Danışmanlık Dışı'
            };

            if (editingLead) {
                const { error } = await supabase
                    .from('testprep_leads')
                    .update(saveFields)
                    .eq('id', editingLead.id);
                if (error) throw error;
                toast.success('Kayıt güncellendi.');
            } else {
                const { error } = await supabase
                    .from('testprep_leads')
                    .insert([{
                        ...saveFields,
                        created_by: user?.id
                    }]);
                if (error) throw error;
                toast.success('Yeni kayıt eklendi.');
            }

            setShowModal(false);
            setEditingLead(null);
            fetchLeads();
        } catch (error: any) {
            console.error('Error saving lead:', error);
            toast.error('Kaydedilirken hata oluştu.');
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
        try {
            const { error } = await supabase.from('testprep_leads').delete().eq('id', id);
            if (error) throw error;
            toast.success('Kayıt silindi.');
            fetchLeads();
            selectedRows.delete(id);
            setSelectedRows(new Set(selectedRows));
        } catch (error) {
            console.error('Error deleting lead:', error);
            toast.error('Silinirken hata oluştu.');
        }
    };

    const handleAddClick = () => {
        setEditingLead(null);
        setFormData({
            full_name: '',
            category: 'Öğrenci',
            contact_date: new Date().toISOString().split('T')[0],
            phone: '',
            email: '',
            communication_type: 'Telefon',
            interested_exam: 'SAT',
            request_type: 'Kurs',
            notes: '',
            is_registered: 'Hayır',
            advising_type: 'Danışmanlık Dışı'
        });
        setShowModal(true);
    };

    const handleEditClick = (lead: TestPrepLead) => {
        setEditingLead(lead);
        setFormData(lead);
        setShowModal(true);
    };

    const handleToggleRegistration = async (leadId: string, currentStatus: string) => {
        const newStatus = currentStatus === 'Evet' ? 'Hayır' : 'Evet';
        try {
            // Optimistic update
            setLeads(leads.map(l => l.id === leadId ? { ...l, is_registered: newStatus } : l));
            
            const { error } = await supabase
                .from('testprep_leads')
                .update({ is_registered: newStatus })
                .eq('id', leadId);

            if (error) {
                // Revert on error
                setLeads(leads.map(l => l.id === leadId ? { ...l, is_registered: currentStatus } : l));
                throw error;
            }
            toast.success(newStatus === 'Evet' ? 'Aday kayıt edildi olarak işaretlendi.' : 'Aday kaydı iptal edildi.');
        } catch (error) {
            console.error('Error toggling registration:', error);
            toast.error('Durum güncellenirken hata oluştu.');
        }
    };

    const handleConvertToStudent = async (lead: TestPrepLead) => {
        if (!await window.appConfirm(`${lead.full_name} adlı adayı öğrenci olarak kaydetmek istediğinize emin misiniz?`)) return;

        try {
            const nameParts = lead.full_name.trim().split(' ');
            const last_name = nameParts.length > 1 ? nameParts.pop() || '' : '';
            const first_name = nameParts.join(' ') || lead.full_name;

            const studentData: any = {
                first_name,
                last_name,
                email: lead.email || '',
                phone: lead.category === 'Öğrenci' ? lead.phone : '',
                parent_name: lead.category === 'Veli' ? lead.full_name : '',
                parent_phone: lead.category === 'Veli' ? lead.phone : '',
                notes: lead.notes || '',
                status: 'active',
                advising_type: lead.advising_type || 'Danışmanlık Dışı'
            };

            const { error } = await supabase
                .from('testprep_students')
                .insert([studentData]);

            if (error) throw error;

            toast.success('Öğrenci başarıyla TestPrep Öğrencileri listesine eklendi.');
            
            // İsteğe bağlı olarak lead durumunu 'Evet' (Kayıtlı) yapabiliriz:
            if (lead.is_registered !== 'Evet') {
                await handleToggleRegistration(lead.id, 'Hayır'); // Toggle it to Evet
            }
            
        } catch (error: any) {
            console.error('Error converting lead to student:', error);
            toast.error('Öğrenciye dönüştürülürken hata oluştu: ' + error.message);
        }
    };

    const toggleRowCheck = (id: string) => {
        const newSet = new Set(selectedRows);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedRows(newSet);
    };

    const toggleAllCheck = () => {
        if (selectedRows.size === filteredLeads.length) {
            setSelectedRows(new Set());
        } else {
            setSelectedRows(new Set(filteredLeads.map(l => l.id)));
        }
    };

    const handleOpenSmsModal = () => {
        if (selectedRows.size === 0) {
            toast.error('Lütfen mesaj göndermek için listeden en az bir kişi seçin.');
            return;
        }
        setShowSmsModal(true);
    };

    const handleSendBulkSms = async () => {
        if (!smsMessage.trim()) return toast.error('Mesaj boş olamaz.');
        if (selectedRows.size === 0) return toast.error('Lütfen mesaj gönderilecek kişileri seçin.');

        setSendingSms(true);
        try {
            const phones = leads
                .filter(l => selectedRows.has(l.id) && l.phone)
                .map(l => l.phone.replace(/\D/g, ''));

            if (phones.length === 0) {
                toast.error('Seçilen kişilerin geçerli telefon numarası yok.');
                setSendingSms(false);
                return;
            }

            const { error } = await supabase.functions.invoke('send-sms', {
                body: {
                    phones,
                    message: smsMessage,
                    userId: user?.id,
                    branchId: user?.branchId
                }
            });

            if (error) throw error;

            toast.success(`${phones.length} kişiye mesaj gönderildi.`);
            setShowSmsModal(false);
            setSmsMessage('');
            setSelectedRows(new Set());
        } catch (error) {
            console.error('Error sending sms:', error);
            toast.error('Mesaj gönderilirken hata oluştu.');
        } finally {
            setSendingSms(false);
        }
    };

    const filteredLeads = leads.filter(l =>
        l.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.phone?.includes(searchTerm) ||
        l.interested_exam?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.request_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        TestPrep Leads (Adaylar)
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Potansiyel öğrencileri ve veli görüşmelerini takip edin.</p>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button
                        onClick={handleOpenSmsModal}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors flex-1 sm:flex-none justify-center ${selectedRows.size > 0 ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                    >
                        <MessageSquare className="w-4 h-4" />
                        <span>Toplu Mesaj {selectedRows.size > 0 ? `(${selectedRows.size})` : ''}</span>
                    </button>
                    <button
                        onClick={handleAddClick}
                        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex-1 sm:flex-none justify-center"
                    >
                        <Plus className="w-4 h-4" />
                        <span>Yeni Lead Ekle</span>
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-[calc(100vh-12rem)]">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-4 items-center justify-between">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="İsim, telefon veya sınav ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    <table className="w-full text-sm text-left border-collapse">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-10 border-b border-gray-200 dark:border-gray-800">
                            <tr>
                                <th className="px-4 py-3 w-10">
                                    <input
                                        type="checkbox"
                                        checked={selectedRows.size === filteredLeads.length && filteredLeads.length > 0}
                                        onChange={toggleAllCheck}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </th>
                                <th className="px-4 py-3">Tarih</th>
                                <th className="px-4 py-3">İsim Soyisim</th>
                                <th className="px-4 py-3">İletişim</th>
                                <th className="px-4 py-3">Kategori</th>
                                <th className="px-4 py-3">Sınav / Talep</th>
                                <th className="px-4 py-3">Kayıt</th>
                                <th className="px-4 py-3 text-right">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {loading ? (
                                <tr><td colSpan={8} className="p-8 text-center text-gray-500">Yükleniyor...</td></tr>
                            ) : filteredLeads.length === 0 ? (
                                <tr><td colSpan={8} className="p-8 text-center text-gray-500">Aday bulunamadı.</td></tr>
                            ) : (
                                filteredLeads.map((lead) => (
                                    <tr key={lead.id} className={`group hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-all border-b border-gray-100 dark:border-gray-800/50 ${selectedRows.has(lead.id) ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}>
                                        <td className="px-4 py-4 w-10">
                                            <input
                                                type="checkbox"
                                                checked={selectedRows.has(lead.id)}
                                                onChange={() => toggleRowCheck(lead.id)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-4 py-4 text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                <Clock className="w-4 h-4 text-gray-400" />
                                                {new Date(lead.contact_date).toLocaleDateString('tr-TR')}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/50 dark:to-blue-800/50 flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold shadow-sm border border-blue-200/50 dark:border-blue-700/50">
                                                    {lead.full_name?.substring(0, 2).toUpperCase() || 'L'}
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                        {lead.full_name}
                                                    </div>
                                                    {lead.creator?.name && (
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
                                                            <User className="w-3 h-3 text-gray-400" />
                                                            <span>{lead.creator.name}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="font-medium text-gray-900 dark:text-gray-200 mb-0.5">{lead.phone}</div>
                                            <div className="text-xs text-gray-500 truncate max-w-[150px]" title={lead.email}>{lead.email}</div>
                                            <span className="inline-block mt-1.5 px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-[10px] uppercase font-bold rounded shadow-sm border border-gray-200 dark:border-gray-700">
                                                {lead.communication_type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div>
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${lead.category === 'Öğrenci' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/50' : 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/50'} `}>
                                                    {lead.category}
                                                </span>
                                                {lead.advising_type && (
                                                    <div className="mt-1">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                                                            lead.advising_type === 'Danışmanlık Öğrencisi'
                                                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                                        }`}>
                                                            {lead.advising_type}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="font-bold text-gray-900 dark:text-gray-200">{lead.interested_exam}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">{lead.request_type}</div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center">
                                                <button 
                                                    onClick={() => handleToggleRegistration(lead.id, lead.is_registered)}
                                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${lead.is_registered === 'Evet' ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                                                >
                                                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${lead.is_registered === 'Evet' ? 'translate-x-5' : 'translate-x-1'}`} />
                                                </button>
                                                <span className={`ml-2 text-xs font-semibold ${lead.is_registered === 'Evet' ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                                    {lead.is_registered === 'Evet' ? 'Kayıtlı' : 'Bekliyor'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button
                                                    onClick={() => handleConvertToStudent(lead)}
                                                    className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                                                    title="Öğrenciye Dönüştür"
                                                >
                                                    <UserPlus className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleEditClick(lead)}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                                                    title="Düzenle"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(lead.id)}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                                    title="Sil"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {editingLead ? 'Adayı Düzenle' : 'Yeni Aday Ekle'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">İsim Soyisim</label>
                                    <input
                                        required
                                        type="text"
                                        value={formData.full_name}
                                        onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Kategori</label>
                                    <select
                                        value={formData.category}
                                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    >
                                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">İletişim Tarihi</label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.contact_date}
                                        onChange={e => setFormData({ ...formData, contact_date: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Telefon No</label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">E-posta</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Görüşme Şekli</label>
                                    <select
                                        value={formData.communication_type}
                                        onChange={e => setFormData({ ...formData, communication_type: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    >
                                        {COMMUNICATION_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">İlgilendiği Sınav</label>
                                    <select
                                        value={formData.interested_exam}
                                        onChange={e => setFormData({ ...formData, interested_exam: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    >
                                        {EXAMS.map(e => <option key={e} value={e}>{e}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Talep</label>
                                    <select
                                        value={formData.request_type}
                                        onChange={e => setFormData({ ...formData, request_type: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    >
                                        {REQUEST_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Kayıt</label>
                                    <select
                                        value={formData.is_registered}
                                        onChange={e => setFormData({ ...formData, is_registered: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    >
                                        <option value="Evet">Evet</option>
                                        <option value="Hayır">Hayır</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Danışmanlık Durumu</label>
                                    <select
                                        value={formData.advising_type || 'Danışmanlık Dışı'}
                                        onChange={e => setFormData({ ...formData, advising_type: e.target.value })}
                                        className="w-full p-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    >
                                        <option value="Danışmanlık Öğrencisi">Danışmanlık Öğrencisi</option>
                                        <option value="Danışmanlık Dışı">Danışmanlık Dışı</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-2 border-t border-gray-100 dark:border-gray-800 pt-4 mt-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notlar</label>
                                <textarea
                                    className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 min-h-[100px] text-gray-900 dark:text-white"
                                    value={formData.notes}
                                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Görüşme notlarınızı buraya yazabilirsiniz..."
                                />
                            </div>
                            <div className="flex justify-end pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="mr-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center"
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    Kaydet
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk SMS Modal */}
            {showSmsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Toplu Mesaj Gönder</h2>
                            <button onClick={() => setShowSmsModal(false)} className="text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 space-y-4">
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg flex items-start gap-3">
                                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                                <div>
                                    <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">Toplam {selectedRows.size} kişi seçildi.</p>
                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Sadece geçerli telefon numarası olan adaylara mesaj gönderilecektir.</p>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mesajınız</label>
                                <textarea
                                    className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 min-h-[120px] text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 outline-none"
                                    value={smsMessage}
                                    onChange={e => setSmsMessage(e.target.value)}
                                    placeholder="Göndermek istediğiniz mesajı buraya yazın..."
                                />
                            </div>
                            <div className="flex justify-end pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowSmsModal(false)}
                                    className="mr-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleSendBulkSms}
                                    disabled={sendingSms}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center disabled:opacity-50"
                                >
                                    {sendingSms ? (
                                        <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></span>
                                    ) : (
                                        <MessageSquare className="w-4 h-4 mr-2" />
                                    )}
                                    Gönder
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TestPrepLeads;
